<template>
  <div>
    <b-modal
      v-model="showHotel"
      modal-class="dark-modal"
      content-class="mb-12"
      centered
      :hide-footer="false"
      :hide-header="false"
      size="sxl"
      @hidden="closeHotel"
    >
      <template #modal-header>
        <strong>
          {{ hotelName }}
        </strong>
        <div
          v-rtg-tooltip="$t('close')"
          class="btn-link modal-close-button"
          @click="closeHotel"
          @touchstart="closeHotel"
        >
          <fa icon="times" />
        </div>
      </template>
      <template #modal-footer>
        <a
          class="text-white link-underline"
          :data-i18n="footerText"
          @click="closeHotel"
          @touchstart="closeHotel"
        >{{ $t(footerText) }}</a>
      </template>
      <div
        v-if="error"
        class="alert mt-4 lg:mt-5"
      >
        <div class="flex flex-wrap -mx-4">
          <div class="w-12/12 md:w-4/12 xl:w-3/12 px-4 text-center">
            <fa
              class="fa-w-16 fa-7x"
              :icon="icons.faUmbrellaBeach"
            />
          </div>
          <div class="w-12/12 md:w-8/12 xl:w-9/12 px-4 flex items-center">
            <strong data-i18n="charterHotelError">
              {{ $t('charterHotelError') }}
            </strong>
          </div>
        </div>
      </div>
      <div v-if="loadingInfo && !error">
        <HotelPreviewGallerySkeleton />
        <div class="p-3 lg:p-4">
          <div class="flex flex-wrap -mx-4">
            <div class="md:w-10/12 px-4">
              <b-skeleton
                height="1.5rem"
                width="40%"
              />
              <div class="mt-4" />
              <b-skeleton />
              <b-skeleton />
              <b-skeleton />
              <b-skeleton />
            </div>
            <div class="md:w-2/12 px-4 flex items-center justify-center">
              <b-skeleton
                type="button"
                width="75%"
              />
            </div>
            <div class="w-12/12">
              <div class="mt-6" />
              <b-skeleton
                height="1.5rem"
                width="25%"
              />
              <div class="mt-4" />
              <b-skeleton-img aspect="3:1" />
            </div>
          </div>
        </div>
      </div>
      <div v-show="!loadingInfo">
        <HotelPreviewGallery
          :hotel="hotel"
          :clickable-gallery="true"
          @open-gallery="openGallery"
        />
        <div class="absolute top-left image__top-left">
          <div class="flex flex-col items-start mb-2">
            <CampaignTag
              v-for="tag in filteredTags"
              :key="`tag-b-${tag.id}`"
              class="mb-2"
              :tag="tag"
              :show-icon="true"
              size="md"
            />
          </div>
        </div>
        <div class="p-3 lg:p-4">
          <div class="flex flex-wrap -mx-4">
            <div class="md:w-10/12 px-4">
              <h5 class="capitalize mb-0">
                <span>
                  {{ hotelName }}
                </span>
                <div class="inline-block ml-2">
                  <div class="pb-0 product__testimonials__stars">
                    <div
                      v-for="i in parseInt(hotel?.accommodation_rating || hotel?.meta?.Rating || 0)"
                      :key="i"
                      class="review-star c-orange"
                    >
                      <StarByLocale :size="16" />
                    </div>
                  </div>
                </div>
              </h5>
              <div
                class="my-4 whitespace-pre-wrap"
                v-html="hotelInfo.meta.Description"
              />
              <div
                v-if="hotel.facilities_text"
                class="flex"
              >
                <div class="mr-2">
                  <fa
                    class="text-[20px]"
                    :icon="icons.faMapPin"
                  />
                </div>
                <div>
                  <span class="font-bold">
                    {{ $t('hotelNearbyFacilities') }}
                  </span>
                  <span
                    class="ml-2"
                    v-html="hotel.facilities_text"
                  />
                </div>
              </div>
            </div>
            <div class="md:w-2/12 px-4 flex items-center justify-center">
              <button
                class="btn btn-blue btn-xl btn-rounded"
                data-i18n="formChooseHotel"
                @click="openRooms"
              >
                {{ $t('formChooseHotel') }}
              </button>
            </div>
            <LazyMultiVideoPlayer
              class="my-4 px-4"
              :movies="hotelInfo?.meta?.movies"
            />
            <div class="w-12/12 px-4">
              <h5 data-i18n="hotelMapTitle">
                {{ $t('hotelMapTitle') }}
              </h5>
              <div class="map-modal">
                <div class="map overflow-clip">
                  <Mapbox
                    ref="map"
                    :key="hotelId"
                    class="relative"
                    :center="coordinates"
                    :zoom="14"
                    size-to-parent-bounds
                  >
                    <MapboxMarker :coordinates="coordinates" />
                    <MapboxNavigationControl />
                  </Mapbox>
                </div>
              </div>
              <div
                v-if="hotel.nearby_facilities_text"
                class="mt-6"
              >
                <span
                  class="font-bold"
                  data-i18n="hotelNearbyFacilities"
                >{{ $t('hotelNearbyFacilities') }}</span>
                <span
                  class="ml-2"
                  v-html="hotel.nearby_facilities_text"
                />
              </div>
              <div
                v-if="facilities.length"
                class="mt-6"
              >
                <h5 data-i18n="hotelFacilities">
                  {{ $t('hotelFacilities') }}
                </h5>
                <div
                  v-for="(facilityList, index) in facilities"
                  :key="`fl_${index}`"
                >
                  <strong>
                    {{ facilityList.name }}
                  </strong>
                  <ul class="columns-3">
                    <li
                      v-for="(facility, findex) in facilityList.facilities"
                      :key="`f_${index}_${findex}`"
                    >
                      <fa
                        v-if="facility.indYesOrNo === false || facility.indLogic === false"
                        class="mr-2 text-error"
                        :icon="icons.faTimesCircle"
                      />
                      <fa
                        v-if="facility.indYesOrNo === true || facility.indLogic === true"
                        class="mr-2 text-green"
                        :icon="icons.faCheckCircle"
                      />
                      <span>
                        {{ facility.text }}
                      </span>
                      <span
                        v-if="facility.timeFrom"
                        class="ml-2"
                      >{{ facilityTime(facility.timeFrom) }}<span
                        v-if="facility.timeTo && facility.timeTo !== facility.timeFrom"
                        class="ml-2"
                      >- {{ facilityTime(facility.timeTo) }}</span>
                      </span>
                      <span
                        v-else-if="facility.timeTo"
                        class="ml-2"
                      >{{ facilityTime(facility.timeTo) }}</span>
                      <span
                        v-if="facility.indFee"
                        class="facility-fee ml-2"
                        data-i18n="hotelFacilityFee"
                      >{{ $t('hotelFacilityFee') }}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <TripAdvisorInfo
                ref="tripadvisor"
                :data="hotelInfo?.tripadvisor_data"
                :pretty="!$isSol"
              />
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <FullscreenImageCarousel
      v-if="showGallery"
      :images="roomImages || images"
      :visible="showGallery"
      @on-hide="closeGallery"
    />
    <HotelModalRooms
      :error="error"
      :loading-booking="loadingBooking"
      :error-booking="errorBooking"
      :selected-rooms="selectedRooms"
      :book-type="bookType"
      :dealfinder="dealfinder"
      @close-rooms="closeRooms"
      @open-gallery="openGallery"
      @unselect-rooms="$emit('unSelectRooms')"
      @select-room="(d) => $emit('selectRoom', d)"
      @go-next="goNext"
      @close-hotel="showHotel = false"
    >
      <template #title>
        <strong>
          {{ hotelName }}
        </strong>
        <AgentComponent>
          <span class="text-gray ml-1">
            ({{ hotel.giata_id }})
          </span>
        </AgentComponent>
      </template>
      <template #pricing="{ totalRoomPrices }">
        <slot
          name="roomPrice"
          :total-room-prices="totalRoomPrices"
        />
      </template>
    </HotelModalRooms>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { faUmbrellaBeach } from '@fortawesome/pro-duotone-svg-icons'
import { faTimesCircle, faCheckCircle, faMapPin } from '@fortawesome/pro-regular-svg-icons'

import charterHotelMixin from '@layers/web/mixins/charter-hotel.mixin'
import events from '@layers/web/constants/events.js'

export default defineNuxtComponent({
  setup () {
    const { trackViewContent, trackAddToCart } = useTracking()

    return {
      trackViewContent,
      trackAddToCart,
    }
  },

  mixins: [charterHotelMixin],

  props: {
    selectedRooms: {
      type: Array,
      required: true,
    },

    hotels: {
      type: Array,
      required: true,
    },

    bookType: {
      type: String,
      default: 'book',
    },

    errorBooking: {
      type: Boolean,
      default: false,
    },

    requestState: {
      type: Boolean,
      default: false,
    },

    dealfinder: {
      type: Boolean,
      deafault: false,
    },
  },

  emits: [
    'close',
    'goNext',
    'selectHotel',
    'unSelectRooms',
    'selectRoom',
    events.showHotelModalRooms,
    events.closeHotelModalRooms,
    events.hideModal,
  ],

  data () {
    return {
      icons: {
        faUmbrellaBeach,
        faMapPin,
        faTimesCircle,
        faCheckCircle,

      },

      error: false,
      loadingInfo: false,
      loadingBooking: false,

      hotelId: null,
      hotelInfo: { meta: {} },

      roomImages: null,

      showHotel: false,
      showGallery: false,
      hgKey: 1,

      mapKey: 1,
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false,
      },

      timer: null,
    }
  },

  computed: {
    ...mapState(useLocaleStore, ['locale']),

    ...mapState(useCharterStore, {
      previousHotelId: 'modalHotelId',
    }),
    ...mapState(useCharterPackagesStore, ['getCharterTag']),

    ...mapState(useRootStore, {
      showOfficeTools: state => state.showOfficeTools,
    }),

    filteredTags () {
      return (this.hotel?.charter_tags || [])
        .filter(charterTagIsNotCampaign)
    },

    hotel () {
      if (!this.hotelId) {
        return { meta: {} }
      }
      if (typeof this.hotelId !== 'string') {
        this.$sentrySetContext('details', JSON.stringify({ id: this.hotelId, hotel: this.hotel }))
        this.$sentryCaptureException('book/hotelModal > hotelId was set but not a string', {
          level: 'info',
          tags: {
            type: 'UX',
            source: 'booking',
          },
        })

        return { meta: {} }
      }

      let hotel

      const hotels = (this.hotels || [])
      const dateOptionParts = (this.hotelId || '').split('_')
      if (dateOptionParts && dateOptionParts.length >= 3) {
        // package hotel id
        const dateOption = dateOptionParts[2]
        const parsedHotelId = this.hotelId?.replace(/(_\d+)$/, '')
        const foundHotel = hotels.find(h => h.id + '' === parsedHotelId)
        if (foundHotel && !!foundHotel.dateOptions && !!foundHotel.dateOptions[dateOption]) {
          hotel = foundHotel.dateOptions[dateOption]
        }
      } else {
        const [, hotelCode] = dateOptionParts
        hotel = hotels
          // eslint-disable-next-line camelcase
          .find(({ hotel_code, id }) => `${id}` === this.hotelId || `${hotel_code}` === hotelCode)
      }

      if (!hotel) {
        return { meta: {} }
      } else if (!hotel.meta) {
        hotel.meta = {}
      }
      return {
        ...hotel,
        charter_tags: (hotel?.charter_tags || [])
          .map(tagId => this.getCharterTag(tagId)),

        meta: {
          ...hotel.meta,
          ...this.hotelInfo.meta,
        },
      }
    },

    hotelName () {
      const name = (this.hotel.hotel || this.hotel.meta.PropertyName || this.hotel.propertyName || '')
      const suffix = (Object.prototype.hasOwnProperty.call(this.hotel, 'giataId') && this.showOfficeTools) ? ` [${this.hotel.giataId}]` : ''
      return name + suffix
    },

    coordinates () {
      return {
        lat: parseFloat(this.hotel.meta?.Latitude || 0),
        lng: parseFloat(this.hotel.meta?.Longitude || 0),
      }
    },

    facilities () {
      if (Object.keys(this.hotelInfo.meta?.Facilities_grouped || {}).length === 0) {
        return []
      }

      const res = Object.keys(this.hotelInfo.meta?.Facilities_grouped || {})
        .filter(k => k.startsWith(7) && this.hotelInfo.meta?.Facilities_grouped[k])
        .map(g => ({
          name: this.hotelInfo.meta.Facilities_grouped[g].facilityGroupText,
          facilities: this.hotelInfo.meta.Facilities_grouped[g].facilities,
        }))

      if (this.hotelInfo.meta?.Facilities_grouped[30]) {
        res.push({
          name: this.$t('hotelAcceptedPayments'),
          facilities: this.hotelInfo.meta.Facilities_grouped[30].facilities,
        })
      }

      return res
    },

    footerText () {
      return this.dealfinder ? 'hotelBackToCalendar' : 'hotelBackToList'
    },
  },

  watch: {
    '$route.query.showRooms' () {
      if (this.$route.query.showRooms) {
        if (!this.hotels.length) {
          const query = { ...this.$route.query }
          delete query.showRooms

          return this.$router.replace({ query }).catch(() => { })
        }

        this.showRoomsModal()
      } else if (!this.$route.query.showHotel) {
        this.hotelId = null
        this.hotelInfo = { meta: {} }
      }

      if (!this.$route.query.showRooms) {
        this.closeRooms()
      }
    },

    '$route.query.showHotel' () {
      if (this.$route.query.showHotel) {
        if (!this.hotels.length) {
          const query = { ...this.$route.query }
          delete query.showHotel

          return this.$router.replace({ query }).catch(() => { })
        }

        this.showHotelModal()
      } else if (!this.$route.query.showRooms) {
        this.hotelId = null
        this.showHotel = false
        this.closeRooms()
      }
    },

    '$route.query.hotelGallery' (v) {
      if (v) {
        this.showGallery = true

        return
      }

      this.showGallery = false
    },
  },

  mounted () {
    const query = { ...this.$route.query }
    if ((this.$route.query.showHotel || this.$route.query.showRooms) && !this.hotels.length) {
      delete query.showHotel
      delete query.showRooms

      return this.$router.replace({ query }).catch(() => { })
    }

    if (this.$route.query.hotelGallery) {
      delete query.hotelGallery

      this.$router.replace({ query }).catch(() => { })
    }
    if (this.$route.query.showHotel) {
      this.showHotelModal()
    }
    if (this.$route.query.showRooms) {
      this.showRoomsModal()
    }
  },

  beforeUnmount () {
    clearTimeout(this.timer)
  },

  methods: {
    ...mapActions(useCharterStore, {
      bookHotelInfo: 'hotelInfo',
    }),

    ...mapActions(useCharterStore, {
      charterHotelInfo: 'hotelInfo',
    }),

    ...mapActions(useCharterPackagesStore, {
      charterpackagesHotelInfo: 'hotelInfo',
    }),

    showHotelModal () {
      this.hotelId = this.$route.query.showHotel
      this.showHotel = true
      this.$nextTick(() => {
        this.loadInfo()
      })

      this.trackViewContent({
        name: this.hotel.hotel,
        value: this.hotel.pricepp,
        id: this.hotel.hotel_code ?? this.hotel.id,
      })
    },

    showRoomsModal () {
      this.hotelId = this.$route.query.showRooms
      this.$nextTick(() => {
        this.$event.$emit(events.showHotelModalRooms, this.hotel)
      })
    },

    async loadInfo () {
      this.hotelInfo = { meta: {} }
      this.error = false
      this.loadingInfo = true

      const giataId = this.hotel?.giataId || this.hotel?.giata_id

      if (!giataId) {
        this.$sentrySetContext('hotel', JSON.stringify({ hotel: this.hotel }))
        this.$sentryCaptureException(`${this.bookType}/hotelInfo could not find hotels giataId`, {
          level: 'error',
          tags: {
            type: 'UX',
          },
        })

        this.error = true
        return
      }
      try {
        const result = await this[`${this.bookType}HotelInfo`](giataId)

        if (!this.showHotel) {
          return
        }

        this.hotelInfo = result
        this.loadingInfo = false
      } catch (e) {
        if (e.data?.error) {
          if (e.data.message === 'state_expired') {
            this.$event.$emit(events.closeHotelModalRooms)
            return
          }

          this.$sentrySetContext('data', JSON.stringify({ hotel: this.hotel, error: e.data }))
          this.$sentryCaptureException(`${this.bookType}/hotelInfo general error`, {
            level: 'error',
            tags: {
              type: 'UX',
            },
          })

          this.error = true
          return
        }

        this.error = true
      }

      this.$nextTick(() => {
        this.loadMapInfo()
      })
    },

    loadMapInfo () {
      if (!this.showHotel) {
        return
      }

      this.$refs?.map?.onload(() => {
        this.$refs?.map?.resizeMap()
        this.$refs?.map?.panTo(this.coordinates)
      })

      this.$nextTick(() => {
        if (this.$route.query.reviews && this.$refs.tripadvisor) {
          this.timer = window.setTimeout(() => {
            if (this.$refs.tripadvisor) {
              this.$refs.tripadvisor?.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 500)
        }
      })
    },

    openGallery (images) {
      this.roomImages = null
      if (images && Array.isArray(images)) {
        this.roomImages = images
      }
      this.$router.push({
        query: {
          ...this.$route.query,
          hotelGallery: true,
        },
      }).catch(() => { })
    },

    closeGallery () {
      if (this.$route.query.hotelGallery) {
        const query = { ...this.$route.query }
        delete query.hotelGallery

        return this.$router.go(-1)
      } else {
        this.$event.$emit(events.hideModal)
      }
      this.$emit('close')
    },

    openRooms () {
      this.$emit('selectHotel', this.hotelId)
      if (this.hotelId !== this.previousHotelId) {
        this.$emit('unSelectRooms')
      }
    },

    closeRooms () {
      this.loadingBooking = false
      if (this.$route.query.showRooms) {
        const query = { ...this.$route.query }
        delete query.showRooms

        this.$event.$emit(events.closeHotelModalRooms)

        return this.$router.replace({ query }).catch(() => { })
      } else {
        this.$event.$emit(events.closeHotelModalRooms)
      }
      this.$emit('close')
    },

    facilityTime (time) {
      return time.match(/^\d+:\d+/)[0]
    },

    goNext () {
      this.loadingBooking = true

      if (!this.$isSol) {
        this.$event.$emit(events.closeHotelModalRooms)
      }

      this.$emit('goNext')

      this.trackAddToCart({
        name: this.hotel.hotel,
        id: this.hotel.hotel_code ?? this.hotel.id,
        value: this.totalRoomPrices,
      })
    },

    percentage (curr, total) {
      return (100 * parseFloat(curr)) / parseFloat(total)
    },

    // setFeaturedImageToFallback () {
    //   this.$refs.featuredImage.style.backgroundImage = `url('${this.imageSize(this.featuredImg, 'thumbnail')}')`
    // },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/_hotel-gallery";
@import "@layers/web/assets/scss/pages/_book";
@import "@layers/web/assets/scss/modules/product/_tripadvisor";
@import "@layers/web/assets/scss/modules/product/_product-testimonials";

.gallery-modal {
  overflow: hidden;

  .modal-content {
    background: transparent;
    border: none;

    height: 100%;
  }

  .modal-backdrop {
    opacity: 0.8;
  }

  .modal-body {
    padding: 0;
  }
}
</style>
